import React, { useState, useEffect } from 'react';
import { Volume2, Camera, Home, MoreVertical } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface FloatingButtonsProps {
  onHomeClick?: () => void;
  showBottomHomeButton?: boolean;
}

const FloatingButtons: React.FC<FloatingButtonsProps> = ({ 
  onHomeClick,
  showBottomHomeButton = true 
}) => {
  const router = useRouter();
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get responsive icon sizes
  const getSmallIconSize = () => {
    if (isMobile) return 18; // Larger for mobile touch targets
    return 20; // Desktop size
  };

  const getHomeIconSize = () => {
    if (isMobile) return 22; // Larger home icon for mobile
    return 24; // Desktop home icon
  };

  const handleHomeClick = () => {
    if (onHomeClick) {
      onHomeClick();
    } else {
      router.push('/');
    }
  };

  return (
    <>      {/* Top right buttons */}
      <div
        className={`fixed ${isMobile ? 'top-4 right-2' : 'top-5 right-2'}`}
        style={{
          width: isMobile ? "96px" : "112px",
          height: isMobile ? "280px" : "320px",
        }}
      >
        <div className="flex flex-col items-center gap-2">
          <button className={`${isMobile ? 'w-16 h-16' : 'w-20 h-20'} rounded-full bg-eurotile text-white flex items-center justify-center shadow-glassmorphism hover:scale-105 transition-transform duration-200`}>
            <img
              src="/logo/logo.png"
              alt=""
              className={`${isMobile ? 'w-16 h-16' : 'w-20 h-20'} object-contain rounded-full`}
            />
          </button>
          <button className={`${isMobile ? 'w-9 h-9' : 'w-10 h-10'} rounded-full bg-white text-gray-600 flex items-center justify-center shadow-button-drop-shadow hover:scale-105 hover:bg-gray-50 transition-all duration-200`}>
            <Volume2 size={getSmallIconSize()} />
          </button>
          <button className={`${isMobile ? 'w-9 h-9' : 'w-10 h-10'} rounded-full bg-white text-gray-600 flex items-center justify-center shadow-button-drop-shadow hover:scale-105 hover:bg-gray-50 transition-all duration-200`}>
            <Camera size={getSmallIconSize()} />
          </button>
          <button className={`${isMobile ? 'w-9 h-9' : 'w-10 h-10'} rounded-full bg-white text-gray-600 flex items-center justify-center shadow-button-drop-shadow hover:scale-105 hover:bg-gray-50 transition-all duration-200`}>
            <MoreVertical size={getSmallIconSize()} />
          </button>
        </div>
      </div>      {/* Bottom right buttons */}
      <div className={`fixed ${isMobile ? 'bottom-3 right-3' : 'bottom-4 right-4'}`}>
        <button className={`${isMobile ? 'w-11 h-11' : 'w-12 h-12'} rounded-full bg-blue-500 text-white flex items-center justify-center shadow-glassmorphism hover:scale-105 hover:bg-blue-600 transition-all duration-200`}>
          <img src="/placeholder.svg" alt="" className={`${isMobile ? 'w-7 h-7' : 'w-8 h-8'}`} />
        </button>
      </div>
      
      {/* Bottom center home button */}
      {showBottomHomeButton && (
        <div className={`fixed ${isMobile ? 'bottom-3' : 'bottom-4'} left-1/2 transform -translate-x-1/2 flex space-x-3`}>
          <button 
            onClick={handleHomeClick}
            className={`${isMobile ? 'w-11 h-11' : 'w-12 h-12'} rounded-full bg-white text-gray-600 flex items-center justify-center shadow-glassmorphism hover:scale-105 hover:bg-gray-50 hover:text-blue-600 transition-all duration-200`}
            aria-label="Go to home page"
          >
            <Home size={getHomeIconSize()} />
          </button>
        </div>
      )}</>
  );
};

export default FloatingButtons;
